import { useRef } from "react";
import { SegmentedControl } from "../ui/SegmentedControl";
import {
  BLOCK_LABELS,
  FOOTER_TEXT,
  type ReceiptAlign,
  type ReceiptBlock,
  type ReceiptBlockId,
  type ReceiptSize,
  type ReceiptTemplate,
} from "../../receipt/receiptTemplate";

/**
 * G-64 — arranging the blocks of a ticket.
 *
 * REORDER, REMOVE, PUT BACK, ALIGN, SIZE — and one free-text message. Not a
 * canvas: OD-13 says the ticket must never impersonate an invoice, and a blank
 * canvas makes that unenforceable by construction. The owner approved this
 * shape over the canvas on 2026-09-11.
 *
 * TWO LISTS, NOT A COLUMN OF CHECKBOXES. The first version hid a block by
 * unticking a box, and the owner looked at the finished screen and said the
 * blocks had no remove button — which was true of the affordance even though it
 * was false of the behaviour. So "on the ticket" and "not on the ticket" are now
 * two places, and the button in each says what pressing it does. Nothing is
 * destroyed: the block set is fixed, so a removed block has to remain
 * retrievable or a shop could lose its total with no way back.
 *
 * Moving is done with buttons rather than dragging. This lives on a shop tablet
 * operated with a thumb, where a drag needing a grab handle and a drop target
 * is a worse instrument than "up" and "down".
 *
 * The footer is deliberately in neither list. It is not a block a shop owns;
 * it is shown at the bottom, greyed, so nobody hunts for the control to move it.
 */

const ALIGNS: Array<{ value: ReceiptAlign; label: string }> = [
  { value: "left", label: "Left" },
  { value: "center", label: "Centre" },
  { value: "right", label: "Right" },
];

const SIZES: Array<{ value: ReceiptSize; label: string }> = [
  { value: "small", label: "S" },
  { value: "normal", label: "M" },
  { value: "large", label: "L" },
];

/** Matches the server's cap, which is the one that actually bites. */
const MESSAGE_MAX = 120;

export function ReceiptBuilder({
  template,
  onChange,
  logoUrl,
  onUploadLogo,
  onClearLogo,
  busy,
}: {
  template: ReceiptTemplate;
  onChange: (next: ReceiptTemplate) => void;
  logoUrl: string | null;
  onUploadLogo: (file: File) => void;
  onClearLogo: () => void;
  busy: boolean;
}) {
  const fileInput = useRef<HTMLInputElement | null>(null);

  const printed = template.blocks.filter((b) => b.visible);
  const removed = template.blocks.filter((b) => !b.visible);

  const replace = (id: ReceiptBlockId, patch: Partial<ReceiptBlock>) => {
    onChange({
      ...template,
      blocks: template.blocks.map((b) =>
        b.id === id ? { ...b, ...patch } : b,
      ),
    });
  };

  /**
   * Swaps with the neighbour that is actually PRINTED.
   *
   * Swapping with the literal next array entry would trade places with a
   * removed block, and the ticket would not change — a button that does
   * nothing visible reads as broken.
   */
  const move = (id: ReceiptBlockId, by: -1 | 1) => {
    const order = printed.map((b) => b.id);
    const at = order.indexOf(id);
    const target = at + by;
    if (at < 0 || target < 0 || target >= order.length) return;

    const a = template.blocks.findIndex((b) => b.id === id);
    const c = template.blocks.findIndex((b) => b.id === order[target]);
    const blocks = [...template.blocks];
    [blocks[a], blocks[c]] = [blocks[c], blocks[a]];
    onChange({ ...template, blocks });
  };

  /**
   * Puts a block back at the END of what prints, not wherever it happened to
   * sit in the array. A block reappearing in the middle of the ticket looks
   * like the app moved something on its own; arriving last is predictable, and
   * Up takes it from there.
   */
  const restore = (id: ReceiptBlockId) => {
    const block = template.blocks.find((b) => b.id === id);
    if (!block) return;
    const rest = template.blocks.filter((b) => b.id !== id);
    const lastPrinted = rest.map((b) => b.visible).lastIndexOf(true);
    const blocks = [...rest];
    blocks.splice(lastPrinted + 1, 0, { ...block, visible: true });
    onChange({ ...template, blocks });
  };

  return (
    <div className="receipt-builder">
      <h3 className="receipt-list-heading">On the ticket</h3>
      <ol className="receipt-block-list">
        {printed.map((block, index) => (
          <li key={block.id} className="receipt-block-row">
            <div className="receipt-block-head">
              <span className="receipt-block-name">
                {BLOCK_LABELS[block.id]}
              </span>
              <div className="receipt-block-move">
                <button
                  type="button"
                  className="button plain"
                  onClick={() => move(block.id, -1)}
                  disabled={busy || index === 0}
                  aria-label={`Move ${BLOCK_LABELS[block.id]} up`}
                >
                  Up
                </button>
                <button
                  type="button"
                  className="button plain"
                  onClick={() => move(block.id, 1)}
                  disabled={busy || index === printed.length - 1}
                  aria-label={`Move ${BLOCK_LABELS[block.id]} down`}
                >
                  Down
                </button>
                <button
                  type="button"
                  className="button plain receipt-block-remove"
                  onClick={() => replace(block.id, { visible: false })}
                  disabled={busy}
                  aria-label={`Remove ${BLOCK_LABELS[block.id]} from the ticket`}
                >
                  Remove
                </button>
              </div>
            </div>

            <div className="receipt-block-controls">
              <SegmentedControl
                options={ALIGNS}
                value={block.align}
                onChange={(align) => replace(block.id, { align })}
                ariaLabel={`Alignment for ${BLOCK_LABELS[block.id]}`}
                disabled={busy}
              />
              <SegmentedControl
                options={SIZES}
                value={block.size}
                onChange={(size) => replace(block.id, { size })}
                ariaLabel={`Size for ${BLOCK_LABELS[block.id]}`}
                disabled={busy}
              />
            </div>

            {block.id === "message" ? (
              <div className="receipt-message-field">
                <input
                  type="text"
                  value={block.text ?? ""}
                  maxLength={MESSAGE_MAX}
                  placeholder="¡Gracias por su compra!"
                  onChange={(e) => replace(block.id, { text: e.target.value })}
                  disabled={busy}
                  aria-label="The message printed on every receipt"
                />
                <span className="muted receipt-message-count">
                  {(block.text ?? "").length}/{MESSAGE_MAX}
                </span>
              </div>
            ) : null}

            {block.id === "logo" ? (
              <div className="receipt-logo-field">
                {logoUrl ? (
                  <img
                    src={logoUrl}
                    alt="The shop's logo"
                    className="receipt-logo-thumb"
                  />
                ) : (
                  <p className="muted">
                    No logo yet. The printer has no greys, so a simple shape
                    reads far better than a photograph.
                  </p>
                )}
                <div className="button-row">
                  <button
                    type="button"
                    className="button tinted"
                    onClick={() => fileInput.current?.click()}
                    disabled={busy}
                  >
                    {logoUrl ? "Replace logo" : "Upload a logo"}
                  </button>
                  {logoUrl ? (
                    <button
                      type="button"
                      className="button plain"
                      onClick={onClearLogo}
                      disabled={busy}
                    >
                      Delete logo
                    </button>
                  ) : null}
                </div>
                <input
                  ref={fileInput}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  hidden
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    // Clear the input so choosing the SAME file twice still
                    // fires — re-picking after a failed upload otherwise
                    // gets silence.
                    e.target.value = "";
                    if (file) onUploadLogo(file);
                  }}
                />
                {logoUrl ? (
                  <label className="receipt-logo-scale">
                    <span className="muted">
                      Logo width — {Math.round(template.logoScale * 100)}% of
                      the paper
                    </span>
                    <input
                      type="range"
                      min={20}
                      max={100}
                      step={5}
                      value={Math.round(template.logoScale * 100)}
                      onChange={(e) =>
                        onChange({
                          ...template,
                          logoScale: Number(e.target.value) / 100,
                        })
                      }
                      disabled={busy}
                    />
                  </label>
                ) : null}
              </div>
            ) : null}
          </li>
        ))}
      </ol>

      {/* The third lock (the layout appends it, the server rejects a template
          carrying it). Shown so its absence from the list reads as deliberate
          rather than as something missing. */}
      <p className="receipt-footer-locked muted">
        Always printed last, and not editable: “{FOOTER_TEXT}”
      </p>

      {removed.length > 0 ? (
        <>
          <h3 className="receipt-list-heading">Not on the ticket</h3>
          <ul className="receipt-removed-list">
            {removed.map((block) => (
              <li key={block.id} className="receipt-removed-row">
                <span>{BLOCK_LABELS[block.id]}</span>
                <button
                  type="button"
                  className="button plain"
                  onClick={() => restore(block.id)}
                  disabled={busy}
                  aria-label={`Put ${BLOCK_LABELS[block.id]} back on the ticket`}
                >
                  Add
                </button>
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </div>
  );
}
